import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import lottie from "lottie-web";
import { QRCodeCanvas } from "qrcode.react";
import styles from "./SendConsentComplete.module.scss";
import {
  FiCopy,
  FiLink,
  FiAlertCircle,
  FiCheck,
  FiGrid,
  FiX,
} from "react-icons/fi";
import confetti from "canvas-confetti";

const SUCCESS_LOTTIE = "/Confetti.json";
const NO_EMAIL_LOTTIE = "/bear.json";

function LottieAnimation({ src, className, loop = true }) {
  const containerRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !src) return undefined;

    animationRef.current = lottie.loadAnimation({
      container: containerRef.current,
      renderer: "svg",
      loop,
      autoplay: true,
      path: src,
      rendererSettings: {
        preserveAspectRatio: "xMidYMid meet",
      },
    });

    return () => {
      if (animationRef.current) {
        animationRef.current.destroy();
        animationRef.current = null;
      }
    };
  }, [src, loop]);

  return <div ref={containerRef} className={className} />;
}

export default function SendConsentComplete() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [treatmentData, setTreatmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    const loadConsentBundle = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`consent_bundle/${id}`);
        setTreatmentData(res.data);
      } catch (err) {
        console.error("Error loading consent bundle:", err);
        setTreatmentData(null);
      } finally {
        setLoading(false);
      }
    };

    loadConsentBundle();
  }, [id]);

  const patientEmail = useMemo(() => {
    return (
      treatmentData?.patient?.email_address?.trim() ||
      treatmentData?.patient?.email?.trim() ||
      treatmentData?.email?.trim() ||
      ""
    );
  }, [treatmentData]);

  const hasEmail = Boolean(patientEmail);

  useEffect(() => {
    if (!hasEmail || loading) return undefined;

    const duration = 3000;
    const end = Date.now() + duration;
    let stopped = false;

    const colors = [
      "#ff595e",
      "#ffca3a",
      "#8ac926",
      "#1982c4",
      "#6a4c93",
      "#ff9f1c",
      "#4ecdc4",
    ];

    const frame = () => {
      if (stopped) return;

      confetti({
        particleCount: 10,
        startVelocity: 42,
        spread: 75,
        origin: { x: 0.12, y: Math.random() * 0.45 + 0.15 },
        colors,
      });

      confetti({
        particleCount: 10,
        startVelocity: 42,
        spread: 75,
        origin: { x: 0.88, y: Math.random() * 0.45 + 0.15 },
        colors,
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();

    return () => {
      stopped = true;
    };
  }, [hasEmail, loading]);

  const patientName = useMemo(() => {
    const first =
      treatmentData?.patient?.first_name?.trim() ||
      treatmentData?.patient?.firstname?.trim() ||
      "";

    const last =
      treatmentData?.patient?.last_name?.trim() ||
      treatmentData?.patient?.lastname?.trim() ||
      "";

    return `${first} ${last}`.trim();
  }, [treatmentData]);

  const patientBaseUrl = (process.env.REACT_APP_PATIENT_URL || "").replace(/\/$/, "");
  const link = id ? `${patientBaseUrl}/${id}` : patientBaseUrl;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 1800);
    } catch (error) {
      console.error("Failed to copy link:", error);
      setCopied(false);
      alert("Could not copy the link. Please copy it manually.");
    }
  };

  const handleQrToggle = () => {
    setShowQr((prev) => !prev);
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.loadingCard}>
            <div className={styles.loader} />
            <p>Loading consent details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (hasEmail) {
    return (
      <div className={styles.page}>
        <div className={styles.successOldContainer}>
          <div className={styles.successOldIllustration}>
            <LottieAnimation
              src={SUCCESS_LOTTIE}
              className={styles.successOldLottie}
              loop={true}
            />
          </div>

          <h1 className={styles.successOldTitle}>
            Well done! Your consent form has been emailed
          </h1>

          <div className={styles.successOldCard}>
            <h2>Email sent successfully</h2>

            <p>
              We’ve provided your patient with a secure link to view and complete
              their consent documents. If you’d like to resend the form manually
              via email or text, simply copy the link below.
            </p>

            <p className={styles.successOldLink}>{link}</p>

            <button
              className={styles.successOldCopyBtn}
              onClick={handleCopy}
              type="button"
            >
              {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>

          <div className={styles.actions}>
            <button
              className={styles.primaryBtn}
              onClick={() => navigate(`/summary-new/${id}`)}
              type="button"
            >
              View summary
            </button>

            <button
              className={styles.secondaryBtn}
              onClick={() => navigate("/dashboard")}
              type="button"
            >
              Go to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={`${styles.animationWrapper} ${styles.noEmailAnimation}`}>
          <LottieAnimation
            src={NO_EMAIL_LOTTIE}
            className={styles.lottieAnimation}
            loop={true}
          />
        </div>

        <h1 className={styles.title}>No email address</h1>

        <div className={`${styles.card} ${styles.warningCard}`}>
          <div className={styles.statusHeader}>
            <div className={`${styles.iconBubble} ${styles.warningBubble}`}>
              <FiAlertCircle />
            </div>

            <div>
              <h2>Share manually</h2>

              {patientName && (
                <p className={styles.patientName}>
                  Patient: <strong>{patientName}</strong>
                </p>
              )}
            </div>
          </div>

          <p className={styles.message}>
            This patient has no email saved. Copy the link or use the QR code to
            share manually.
          </p>

          <div className={styles.linkBox}>
            <FiLink className={styles.linkIcon} />
            <p className={styles.link}>{link}</p>
          </div>

          <div className={styles.shareActions}>
            <button className={styles.copyBtn} onClick={handleCopy} type="button">
              {copied ? <FiCheck size={16} /> : <FiCopy size={16} />}
              {copied ? "Copied" : "Copy link"}
            </button>

            <button className={styles.qrBtn} onClick={handleQrToggle} type="button">
              {showQr ? <FiX size={16} /> : <FiGrid size={16} />}
              {showQr ? "Hide QR" : "Show QR"}
            </button>
          </div>

          {showQr && (
            <div className={styles.qrBox}>
              <QRCodeCanvas value={link} size={150} level="H" includeMargin />
              <p>Scan to open consent form</p>
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <button
            className={styles.primaryBtn}
            onClick={() => navigate(`/summary-new/${id}`)}
            type="button"
          >
            View summary
          </button>

          <button
            className={styles.secondaryBtn}
            onClick={() => navigate("/dashboard")}
            type="button"
          >
            Go to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}